import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { facilitiesApi, queryKeys } from '@/lib/api';
import { useAdminFacility } from '@/hooks/useAdminFacility';

export default function AdminFacilitySelector() {
  const { selectedFacility, setSelectedFacility } = useAdminFacility();
  const [isOpen, setIsOpen] = useState(false);

  // Fetch facilities data
  const { data: facilitiesData } = useQuery({
    queryKey: queryKeys.facilities.list(),
    queryFn: () => facilitiesApi.list({ limit: 100 }),
  });

  const facilities = facilitiesData?.facilities || [];
  const activeFacilities = facilities
    .filter(f => f.isActive)
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedFacilityData = activeFacilities.find(f => f.facilityId === selectedFacility);

  return (
    <div className="relative">
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        // Explicit colours: this sits in the sidebar, which is dark in BOTH
        // themes, so the theme's outline button went white-on-white in light mode.
        className="w-48 justify-between border-slate-700 bg-slate-800 text-left text-slate-100 hover:bg-slate-700 hover:text-white"
      >
        <div className="flex items-center">
          <Building2 className="h-4 w-4 mr-2" />
          <span className="truncate">
            {selectedFacilityData?.name || 'Select Facility'}
          </span>
        </div>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-background border border-border rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
          <div className="p-1">
            <button
              onClick={() => {
                setSelectedFacility('');
                setIsOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground rounded-sm"
            >
              <span className="text-muted-foreground">All Facilities</span>
            </button>
            {activeFacilities.map((facility) => (
              <button
                key={facility.facilityId}
                onClick={() => {
                  setSelectedFacility(facility.facilityId);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground rounded-sm ${
                  selectedFacility === facility.facilityId ? 'bg-accent text-accent-foreground' : ''
                }`}
              >
                <div className="font-medium">{facility.name}</div>
                <div className="text-xs text-muted-foreground">{facility.facilityId}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Overlay to close dropdown when clicking outside */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}